import { useLayoutEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, companySoftwareBillingApi as api, isApiError, type CompanySoftwareBillingApi } from "@bw/shared/api";
import { useAuth } from "@bw/shared/hooks";
import type {
  CommercialSoftwareMethodConsentGrantRequest, CommercialSoftwareMethodConsentRevokeRequest,
  OrganizationSoftwareBillingSetupRequest, OrganizationSoftwareBillingConfirmationRequest,
  OrganizationSoftwareMethodSetupRequest, OrganizationSoftwareMethodSetupResult,
  OrganizationSoftwareBillingView, OrganizationSoftwareMethodView, OrganizationSoftwareMethodSetupPage,
} from "@bw/shared/types/generated";
import type {
  CompanyBillingDenial, CompanyMethodChallenge, CompanyMethodPageRequest,
  CompanyMethodSetupOutcome, CompanySoftwareBillingScope,
  CompanySoftwareBillingHooks,
} from "./companyMethodContracts";

type Operation = keyof CompanySoftwareBillingApi;
const key = (scope: CompanySoftwareBillingScope) => ["company-software", scope.actorId, scope.orgId, scope.environment, scope.acceptanceId] as const;
export { key as companyBillingQueryKey };
const scopeId = (scope: CompanySoftwareBillingScope) => JSON.stringify(key(scope));

export class CompanyBillingRequestError extends ApiError {
  constructor(readonly scope: CompanySoftwareBillingScope, readonly operation: Operation, status: number, code: string) {
    super(status, code, "Company billing is unavailable. Review the current request before retrying.");
  }
}

export function companyBillingDenial(error: unknown): CompanyBillingDenial | null {
  if (!(error instanceof CompanyBillingRequestError) || ![401, 403].includes(error.status)) return null;
  return { scope: error.scope, operation: error.operation, category: error.status === 401 ? "identity_or_scope_lost"
    : error.code === "ACTIVE_BAN" && ["setupBilling", "confirmBilling", "grantConsent", "setupMethod", "purchaseModule", "restoreRenewal"].includes(error.operation)
      ? "new_admission_restricted" : "billing_authority_unproven" };
}

function neutralError(error: unknown, scope: CompanySoftwareBillingScope, operation: Operation): CompanyBillingRequestError {
  return new CompanyBillingRequestError(scope, operation, isApiError(error) ? error.status : 503,
    isApiError(error) && error.code === "ACTIVE_BAN" ? "ACTIVE_BAN"
      : isApiError(error) && error.status === 401 ? "UNAUTHORIZED"
      : isApiError(error) && error.status === 403 ? "COMMERCIAL_UNAUTHORIZED" : "COMPANY_BILLING_UNAVAILABLE");
}

export function useScopeLease(scope: CompanySoftwareBillingScope, billingFenceRevision: number): () => void {
  const { user, loading } = useAuth();
  const client = useQueryClient();
  const id = scopeId(scope);
  const live = useRef({ id, active: true, actor: user?.uid, loading, billingFenceRevision });
  live.current = { ...live.current, id, actor: user?.uid, loading, billingFenceRevision };
  useLayoutEffect(() => {
    live.current.active = true;
    return () => {
      live.current.active = false;
      void client.cancelQueries({ queryKey: key(scope) });
      client.removeQueries({ queryKey: key(scope) });
    };
  }, [id, client]);
  return () => {
    if (live.current.billingFenceRevision !== billingFenceRevision)
      throw new CompanyBillingRequestError(scope, "getBilling", 503, "COMPANY_BILLING_EPOCH_CHANGED");
    if (!live.current.active || live.current.id !== id || live.current.loading || live.current.actor !== scope.actorId)
      throw new CompanyBillingRequestError(scope, "getBilling", 401, "COMPANY_SCOPE_CHANGED");
  };
}

export function checkScope(value: { org_id: string; environment: string }, scope: CompanySoftwareBillingScope): void {
  if (!value || value.org_id !== scope.orgId || value.environment !== scope.environment) throw new Error("Company response scope mismatch");
}

export function checkAgreement(value: { org_id: string; environment: string; acceptance_id: string }, scope: CompanySoftwareBillingScope): void {
  checkScope(value, scope);
  if (value.acceptance_id !== scope.acceptanceId) throw new Error("Company response agreement mismatch");
}

export const uuid = (value: unknown) => typeof value === "string" && /^(?!00000000-0000-0000-0000-000000000000$)[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
export function exactKeys(value: unknown, allowed: string[]): void {
  if (!value || typeof value !== "object" || Array.isArray(value) || Object.keys(value).length !== allowed.length
    || !allowed.every(name => Object.prototype.hasOwnProperty.call(value, name))) throw new Error("Invalid company response");
}
function checkBilling(value: OrganizationSoftwareBillingView, scope: CompanySoftwareBillingScope): void {
  exactKeys(value, ["org_id", "environment", "context_state", "customer_configured", "customer_setup"]); checkScope(value, scope);
  if (!["unconfigured", "enabled", "revoked"].includes(value.context_state) || typeof value.customer_configured !== "boolean"
      || value.customer_configured && value.context_state !== "enabled") throw new Error("Invalid company billing facts");
  if (value.customer_setup !== null) {
    exactKeys(value.customer_setup, ["operation_id", "operation_status"]);
    if (!uuid(value.customer_setup.operation_id) || !["pending", "completed", "failed", "uncertain"].includes(value.customer_setup.operation_status))
      throw new Error("Invalid original company customer reference");
  }
  if (value.customer_configured && value.customer_setup?.operation_status !== "completed") throw new Error("Invalid configured customer reference");
}
function checkMethod(value: OrganizationSoftwareMethodView, scope: CompanySoftwareBillingScope): void {
  exactKeys(value, ["org_id", "environment", "acceptance_id", "state", "current_consent"]); checkAgreement(value, scope);
  if (!["consent_required", "setup_required", "setup_pending", "usable", "revoked", "unknown"].includes(value.state)) throw new Error("Invalid company method facts");
  const consent = value.current_consent;
  if (consent !== null) {
    exactKeys(consent, ["consent_id", "decision_sequence", "decision", "revocable"]);
    if (!uuid(consent.consent_id) || !Number.isSafeInteger(consent.decision_sequence) || consent.decision_sequence <= 0
      || !["grant", "revoke"].includes(consent.decision) || typeof consent.revocable !== "boolean"
      || consent.revocable && consent.decision !== "grant") throw new Error("Invalid current company consent");
  }
}
function checkPage(value: OrganizationSoftwareMethodSetupPage, scope: CompanySoftwareBillingScope, page: CompanyMethodPageRequest): void {
  exactKeys(value, ["org_id", "acceptance_id", "environment", "items", "total", "limit", "offset"]); checkAgreement(value, scope);
  if (!Array.isArray(value.items) || !Number.isSafeInteger(value.total) || value.total < 0 || value.limit !== page.limit || value.offset !== page.offset
    || value.items.length > value.limit || value.items.length > Math.max(0, value.total - value.offset)
    || new Set(value.items.map(item => item.operation_id)).size !== value.items.length) throw new Error("Invalid original setup page");
  for (const item of value.items) {
    exactKeys(item, ["operation_id", "created_at", "operation_status", "application"]);
    if (!uuid(item.operation_id) || !Number.isFinite(Date.parse(item.created_at)) || !["pending", "completed", "failed", "uncertain"].includes(item.operation_status)
      || !["pending", "current", "not_current"].includes(item.application)) throw new Error("Invalid original setup reference");
  }
}

export function useScopedQuery<T>(scope: CompanySoftwareBillingScope, billingFenceRevision: number, operation: Operation, page: readonly (number | string | null)[], enabled: boolean, load: () => Promise<T>) {
  const ensure = useScopeLease(scope, billingFenceRevision);
  return useQuery<T, Error>({
    queryKey: [...key(scope), operation, ...page], retry: false, enabled,
    queryFn: async ({ signal }) => {
      try { ensure(); const value = await load(); ensure(); if (signal.aborted) throw new Error("Company query lease changed"); return value; }
      catch (error) { ensure(); throw neutralError(error, scope, operation); }
    },
  });
}

export function useScopedMutation<T, V>(scope: CompanySoftwareBillingScope, billingFenceRevision: number, operation: Operation,
  execute: (request: V, ensure: () => void) => Promise<T>,
) {
  const ensure = useScopeLease(scope, billingFenceRevision);
  return useGuardedScopedMutation(scope, operation, ensure, execute);
}

/** Same request-owned mutation lifecycle with a required genuine scope/selection
 * guard. No default/optional guard; existing mutations pass their actual lease.
 */
export function useGuardedScopedMutation<T, V>(scope: CompanySoftwareBillingScope, operation: Operation, ensure: () => void,
  execute: (request: V, ensure: () => void) => Promise<T>,
) {
  const client = useQueryClient();
  return useMutation<T, Error, V>({ retry: false,
    // React Query retains this actual invocation's lease, even if a rerender
    // installs newer callbacks while the old command is still pending.
    onMutate: () => { ensure(); return ensure; },
    mutationFn: async (request) => {
      try { ensure(); const value = await execute(request, ensure); ensure(); return value; }
      catch (error) { ensure(); throw neutralError(error, scope, operation); }
    },
    onSuccess: async (_data, _request, requestLease) => {
      if (typeof requestLease !== "function") throw new CompanyBillingRequestError(scope, operation, 503, "COMPANY_SCOPE_CHANGED");
      requestLease(); await client.invalidateQueries({ queryKey: key(scope) }); requestLease();
    },
  });
}

/** Strictly project only the public cached outcome; transient material never enters mutation data. */
function methodOutcome(result: OrganizationSoftwareMethodSetupResult, scope: CompanySoftwareBillingScope,
  onChallenge: (challenge: CompanyMethodChallenge) => void, ensure: () => void, expectedOperationId: string | null,
): CompanyMethodSetupOutcome {
  exactKeys(result, ["operation_id", "acceptance_id", "billing", "method", "disposition", "application", "client_secret", "publishable_key"]);
  checkBilling(result.billing, scope); checkMethod(result.method, scope);
  if (result.acceptance_id !== scope.acceptanceId || !uuid(result.operation_id)
    || expectedOperationId !== null && result.operation_id !== expectedOperationId
    || !["requires_payment_method", "requires_confirmation", "requires_action", "processing", "succeeded", "failed", "uncertain"].includes(result.disposition)
    || !["pending", "current", "not_current"].includes(result.application)
    || (result.disposition === "succeeded") === (result.application === "pending")
    || result.application === "current" && (result.method.state !== "usable" || !result.billing.customer_configured))
    throw new Error("Company setup response mismatch");
  const interactive = ["requires_payment_method", "requires_confirmation", "requires_action"].includes(result.disposition);
  if (interactive) {
    const mode = scope.environment === "sandbox" ? "test" : "live";
    if (typeof result.client_secret !== "string" || !result.client_secret.trim() || typeof result.publishable_key !== "string"
      || !new RegExp(`^pk_${mode}_[A-Za-z0-9_]+$`).test(result.publishable_key)) throw new Error("Company challenge unavailable");
    ensure();
    onChallenge({ operation_id: result.operation_id, acceptance_id: result.acceptance_id, billing: result.billing, method: result.method,
      disposition: result.disposition as CompanyMethodChallenge["disposition"], client_secret: result.client_secret, publishable_key: result.publishable_key });
  } else if (result.client_secret !== null || result.publishable_key !== null) throw new Error("Unexpected company challenge");
  return { operation_id: result.operation_id, acceptance_id: result.acceptance_id, billing: result.billing, method: result.method,
    disposition: result.disposition, application: result.application };
}

export function useCompanyBilling(scope: CompanySoftwareBillingScope, billingFenceRevision: number, suspendAutomaticReads: boolean): ReturnType<CompanySoftwareBillingHooks["useCompanyBilling"]> {
  return useScopedQuery(scope, billingFenceRevision, "getBilling", [], !suspendAutomaticReads, async () => { const value = await api.getBilling(scope.orgId, scope.environment); checkBilling(value, scope); return value; });
}
export function useCompanyMethod(scope: CompanySoftwareBillingScope, billingFenceRevision: number, suspendAutomaticReads: boolean): ReturnType<CompanySoftwareBillingHooks["useCompanyMethod"]> {
  return useScopedQuery(scope, billingFenceRevision, "getMethod", [], !suspendAutomaticReads, async () => { const value = await api.getMethod(scope.orgId, scope.acceptanceId, scope.environment); checkMethod(value, scope); return value; });
}
export function useCompanyMethodDisclosure(scope: CompanySoftwareBillingScope, billingFenceRevision: number): ReturnType<CompanySoftwareBillingHooks["useCompanyMethodDisclosure"]> {
  return useScopedQuery(scope, billingFenceRevision, "getDisclosure", [], false, async () => { const value = await api.getDisclosure(scope.orgId, scope.acceptanceId, scope.environment); checkAgreement(value, scope); return value; });
}
export function useCompanyMethodSetups(scope: CompanySoftwareBillingScope, billingFenceRevision: number, suspendAutomaticReads: boolean, page: CompanyMethodPageRequest): ReturnType<CompanySoftwareBillingHooks["useCompanyMethodSetups"]> {
  return useScopedQuery(scope, billingFenceRevision, "listMethodSetups", [page.limit, page.offset], !suspendAutomaticReads, async () => {
    const value = await api.listMethodSetups(scope.orgId, scope.acceptanceId, { ...page, environment: scope.environment });
    checkPage(value, scope, page); return value;
  });
}
export function useSetupCompanyBilling(scope: CompanySoftwareBillingScope, billingFenceRevision: number): ReturnType<CompanySoftwareBillingHooks["useSetupCompanyBilling"]> {
  return useScopedMutation(scope, billingFenceRevision, "setupBilling", async (request: OrganizationSoftwareBillingSetupRequest) => {
    const value = await api.setupBilling(scope.orgId, request); checkBilling(value.billing, scope); return value;
  });
}
export function useResumeCompanyCustomer(scope: CompanySoftwareBillingScope, billingFenceRevision: number): ReturnType<CompanySoftwareBillingHooks["useResumeCompanyCustomer"]> {
  return useScopedMutation(scope, billingFenceRevision, "resumeCustomer", async (operationId: string) => {
    const value = await api.resumeCustomer(scope.orgId, operationId, scope.environment); checkBilling(value, scope); return value;
  });
}
export function useConfirmCompanyBilling(scope: CompanySoftwareBillingScope, billingFenceRevision: number): ReturnType<CompanySoftwareBillingHooks["useConfirmCompanyBilling"]> {
  return useScopedMutation(scope, billingFenceRevision, "confirmBilling", async (request: OrganizationSoftwareBillingConfirmationRequest) => {
    const value = await api.confirmBilling(scope.orgId, request); checkBilling(value.billing, scope); return value;
  });
}
export function useGrantCompanyMethodConsent(scope: CompanySoftwareBillingScope, billingFenceRevision: number): ReturnType<CompanySoftwareBillingHooks["useGrantCompanyMethodConsent"]> {
  return useScopedMutation(scope, billingFenceRevision, "grantConsent", async (request: CommercialSoftwareMethodConsentGrantRequest) => {
    const value = await api.grantConsent(scope.orgId, request); checkAgreement(value, scope); return value;
  });
}
export function useRevokeCompanyMethodConsent(scope: CompanySoftwareBillingScope, billingFenceRevision: number): ReturnType<CompanySoftwareBillingHooks["useRevokeCompanyMethodConsent"]> {
  return useScopedMutation(scope, billingFenceRevision, "revokeConsent", async (request: CommercialSoftwareMethodConsentRevokeRequest) => {
    const value = await api.revokeConsent(scope.orgId, request); checkAgreement(value, scope); return value;
  });
}
export function useSetupCompanyMethod(scope: CompanySoftwareBillingScope, billingFenceRevision: number, onChallenge: (challenge: CompanyMethodChallenge) => void): ReturnType<CompanySoftwareBillingHooks["useSetupCompanyMethod"]> {
  return useScopedMutation(scope, billingFenceRevision, "setupMethod", async (request: OrganizationSoftwareMethodSetupRequest, ensure) =>
    methodOutcome(await api.setupMethod(scope.orgId, request), scope, onChallenge, ensure, null));
}
export function useResumeCompanyMethod(scope: CompanySoftwareBillingScope, billingFenceRevision: number, onChallenge: (challenge: CompanyMethodChallenge) => void): ReturnType<CompanySoftwareBillingHooks["useResumeCompanyMethod"]> {
  return useScopedMutation(scope, billingFenceRevision, "resumeMethod", async (operationId: string, ensure) =>
    methodOutcome(await api.resumeMethod(scope.orgId, operationId), scope, onChallenge, ensure, operationId));
}
